import CookieService from './Cookies/CookieService';
import { RecordingEngineEnum, SessionState, TestElementType } from './Enums';
import Environment from './Environment';
import IdentityService from './IdentityService';
import MessageService from './MessageService';
import ProxyRecorder from './ProxyRecorder';
import SessionService from './SessionService';
import RedirectService from './RedirectService';
import UrlHelper from './UrlHelper';
import Test from './Test';
import { Reasons } from './Reasons/Reasons';
import ErrorService from './ErrorService';

export default class Dispatcher {
    /**
     * Dispatcher constructor
     * @param {SessionService} sessionService
     * @param {RedirectService} redirectService
     * @param {MessageService} messageService
     * @param {IdentityService} identityService
     * @param {CookieService} cookieService
     * @param {Environment} environment
     * @param {UrlHelper} urlHelper
     * @param {ProxyRecorder} proxyRecorder
     * @param {object} options
     * @param {Test} test
     * @param {ErrorService} errorService
     */
    constructor(sessionService, redirectService, messageService, identityService, cookieService, environment, urlHelper, proxyRecorder, options, test, errorService) {
        this.sessionService = sessionService;
        this.redirectService = redirectService;
        this.messageService = messageService;
        this.identityService = identityService;
        this.cookieService = cookieService;
        this.urlHelper = urlHelper;
        this.proxyRecorder = proxyRecorder;
        this.environment = environment;
        this.test = test;
        this.errorService = errorService;

        this.dataCollectionHash = options.dataCollectionHash;
        this.noWebcamRetries = options.noWebcamRetries;
    }

    init(skipBeforeUnload = false) {
        this.interceptWindowClosed(skipBeforeUnload);
        this.interceptVisibilityChange();
        this.interceptSessionUpdateFailed();
    }

    interceptWindowClosed(skipBeforeUnload = false) {
        const { collected, fail, declined } = SessionState;

        if (!skipBeforeUnload) {
            window.addEventListener('beforeunload', () => {
                const { sessionId, lastSessionStateId } = this.sessionService;

                if (sessionId && this.redirectService.needHandleWindowClose()
                    && lastSessionStateId !== collected && lastSessionStateId !== fail && lastSessionStateId !== declined) {
                    return this.messageService.uiMessages.window_close;
                }
            });
        }

        window.addEventListener('unload', (e) => {
            e.preventDefault();
            const { sessionId, lastSessionStateId } = this.sessionService;

            if (sessionId && lastSessionStateId != collected && lastSessionStateId != fail && lastSessionStateId != declined) {
                this.sessionService.changeState(declined, null, 'User closed window');
            }
        });
    }

    interceptVisibilityChange() {
        document.addEventListener('visibilityChange', () => {
            this.sessionService.logMessage('info', 'hidden:' + document.hidden);
        });

        window.addEventListener('focus', () => {
            this.sessionService.logMessage('info', 'onfocus');
        });

        window.addEventListener('blur', () => {
            this.sessionService.logMessage('info', 'onblur');
        });
    }

    interceptSessionUpdateFailed() {
        this.sessionService.on('sessionUpdateFailed', (result) => {
            this.sessionService.logMessage('error', result.errorMessage);
            this.redirectService.handleScreenOut(result.errorMessage);
        });
    }

    // Start our workflow.
    async run() {
        const authenticated = await this.authenticate();
        if (!authenticated) return false;

        const sessionCreated = await this.createSession();
        if (!sessionCreated) return false;

        const environmentChecked = await this.storeEnvironmentCheckResult();
        if (!environmentChecked) return false;

        const recorderInitialized = await this.initRecorder();
        if (!recorderInitialized) return false;

        return true;
    }

    async authenticate() {
        try {
            const authenticateResult = await this.identityService.authenticate();

            if (!authenticateResult.succeeded) {
                this.redirectService.updateRedirectUrl();
                this.messageService.displayMessage(result._result.data.Message);

                return false;
            }
        } catch (e) {
            await this.javascriptError(e, 'authenticate');

            return false;
        }

        return true;
    }

    async createSession(retry = false) {
        try {
            const inclusionMode = this.urlHelper.getParameter('inclusionMode');
            const urlParams = this.urlHelper.filterSearchParams();

            const createSessionResult = await this.sessionService.createSession(this.identityService.participantId, urlParams, inclusionMode, this.identityService.customParticipantId);

            if (createSessionResult.succeeded) {
                const resultData = createSessionResult.data;

                if (resultData.sessionStateID === SessionState.fail) {
                    return false;
                }

                if (console && console.log) {
                    console.log('SESSION_ID: ' + resultData.sessionID);
                }

                await this.sessionService.logMessage('info', 'Session created. page url: ' + window.location.href);

                return true;
            }
            else if (!retry && createSessionResult.status === 500) {
                // todo: sometimes we can not create session due to seldom timeout issues. Lets try again one time.
                await this.createSession(true);
            }
            else {
                this.sessionService.lastSessionStateId = SessionState.fail;
                this.messageService.displayMessage(createSessionResult.errorMessage, undefined, this.identityService.identityKey);

                return false;
            }
        } catch (e) {
            await this.javascriptError(e, 'createSession');

            return false;
        }
    }

    async storeEnvironmentCheckResult() {
        await this.sessionService.changeState(SessionState.checking_environment);

        if (!this.environment.checksPassed) {
            const error = this.environment.getErrorReason();
            const reason = error.reason;

            await this.sessionService.changeState(SessionState.fail, error.reason);

            if (reason.id === Reasons.no_webcam.id) {
                const cookieName = 'NoCamRetryCount_' + this.dataCollectionHash;
                let retryCountStr = this.cookieService.getCookie(cookieName);
                let retryCount = parseInt(retryCountStr, 10);

                if (isNaN(retryCount)) {
                    retryCount = 0;
                }

                retryCount += 1;

                this.cookieService.setCookie(cookieName, retryCount.toString(), 1);

                this.errorService.error(reason.id, reason._messageKey, true, reason, false);
            }
            return false;
        }

        return true;
    }

    async initRecorder() {
        try {
            await this.sessionService.updateSession({ recordingEngine: RecordingEngineEnum.WebRtcOT });

            if (typeof this.onEnvironmentChecked === "function") {
                this.onEnvironmentChecked();
            }

            return true;
        } catch (error) {
            console.warn(error);
            // fixme: start to use getErrorFromEnvironmentalDetectionCheckResults with updated codes
            if (typeof this.onEnvironmentChecked === "function") {
                this.onEnvironmentChecked();
            }

            return false;
        }
    }

    async getTestElements() {
        const getTestElementsResult = await this.sessionService.getTestElementSequences();
        if (getTestElementsResult.succeeded) {
            const elements = getTestElementsResult.data;
            await this.setTestElements(elements);
        } else {
            await this.sessionService.logMessage('error', getTestElementsResult.errorMessage);
            await this.sessionService.changeState(SessionState.fail, null, getTestElementsResult.errorMessage);

            this.messageService.displayMessage(getTestElementsResult.errorMessage, undefined, this.identityService.identityKey);
        }
    }

    async setTestElements(elements) {
        this.test.init(elements);

        const { IMAGE, VIDEO, URL, TEXT } = TestElementType;
        const testElementSequences = elements.elements.filter(e => e.testElementTypeID === IMAGE || e.testElementTypeID === VIDEO || e.testElementTypeID === URL || e.testElementTypeID === TEXT);

        if (testElementSequences.length > 0 && !this.playlistSetuped) {
            this.playlistSetuped = true;
            await this.sessionService.updateSession({ testElementSequenceIds: testElementSequences.map(e => e.testElementSequenceID) });
        }
    }

    async javascriptError(exception, place) {
        place = place ? place + ': ' : '';

        await this.sessionService.logMessage('info', 'Dispatcher_fails.js javascriptError: ' + JSON.stringify(exception) + ' ' + place);

        await this.sessionService.changeState(SessionState.fail, null, place + exception.message);

        await this.sessionService.logMessage('error', place + exception.message);

        this.redirectService.handleScreenOut(this.messageService.uiMessages.error_js);
    }
}
